import React, { useMemo } from 'react';
import {
  Paper,
  Typography,
  Switch,
  FormControlLabel,
  TextField,
  Grid,
  Chip,
  Box,
  Divider,
  Stack,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import {
  caseManagerInsetSurfaceSx,
  caseManagerPalette,
  publicBodyFontFamily,
  publicTextFieldSx,
} from './caseManagerStyles';

const CaseVisibilityPanel = ({ value, onChange, groups = [], provenance = {} }) => {
  const scheduling = value?.scheduling || {};
  const cohort = value?.cohort || {};

  const groupOptions = useMemo(() => {
    return groups.map((group) => ({
      id: group.id,
      name: group.name,
      label: group.name,
      yearLevel: group.metadata?.yearLevel || group.metadata?.cohortYear || null,
    }));
  }, [groups]);

  const selectedGroups = useMemo(() => {
    if (!Array.isArray(cohort.allowedGroupIds)) {
      return [];
    }
    return cohort.allowedGroupIds
      .map((id) => groupOptions.find((group) => group.id === id) || { id, name: 'Unknown group', label: 'Unknown group' })
      .filter(Boolean);
  }, [cohort.allowedGroupIds, groupOptions]);

  const handleSchedulingChange = (field, nextValue) => {
    onChange({
      ...value,
      scheduling: {
        ...scheduling,
        [field]: nextValue,
      },
    });
  };

  const handleCohortChange = (field, nextValue) => {
    onChange({
      ...value,
      cohort: {
        ...cohort,
        [field]: nextValue,
      },
    });
  };

  const publicationState = value?.publication?.state || 'draft';
  const publicationUpdatedAt = value?.publication?.updatedAt
    ? new Date(value.publication.updatedAt).toLocaleString()
    : null;

  return (
    <Paper sx={{ ...caseManagerInsetSurfaceSx, p: 3, display: 'flex', flexDirection: 'column', gap: 2, borderRadius: '22px', boxShadow: 'none' }}>
      <Box>
        <Typography variant="h6" gutterBottom sx={{ color: caseManagerPalette.ink, fontWeight: 800, fontFamily: publicBodyFontFamily }}>
          Visibility & Scheduling
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip label={`Publication: ${publicationState}`} color={publicationState === 'published' ? 'success' : 'default'} size="small" />
          {publicationUpdatedAt && (
            <Typography variant="caption" color="text.secondary">
              Updated {publicationUpdatedAt}
            </Typography>
          )}
        </Stack>
      </Box>

      <Divider textAlign="left">Scheduling window</Divider>

      <FormControlLabel
        control={(
          <Switch
            checked={scheduling.alwaysAvailable !== false}
            onChange={(event) => handleSchedulingChange('alwaysAvailable', event.target.checked)}
          />
        )}
        label="Always available"
      />

      {scheduling.alwaysAvailable === false && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Opens at"
              type="datetime-local"
              value={scheduling.startsAt || ''}
              onChange={(event) => handleSchedulingChange('startsAt', event.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              sx={publicTextFieldSx}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Closes at"
              type="datetime-local"
              value={scheduling.endsAt || ''}
              onChange={(event) => handleSchedulingChange('endsAt', event.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              sx={publicTextFieldSx}
            />
          </Grid>
        </Grid>
      )}

      <TextField
        label="Timezone"
        value={scheduling.timezone || 'UTC'}
        onChange={(event) => handleSchedulingChange('timezone', event.target.value)}
        helperText="IANA timezone identifier (e.g. Europe/London)"
        fullWidth
        sx={publicTextFieldSx}
      />

      <Divider textAlign="left">Audience filters</Divider>

      <FormControlLabel
        control={(
          <Switch
            checked={cohort.allowAllGroups !== false}
            onChange={(event) => handleCohortChange('allowAllGroups', event.target.checked)}
          />
        )}
        label="Allow all groups"
      />

      {cohort.allowAllGroups === false && (
        <Autocomplete
          multiple
          options={groupOptions}
          value={selectedGroups}
          onChange={(_event, nextValue) => handleCohortChange('allowedGroupIds', nextValue.map((group) => group.id))}
          getOptionLabel={(option) => option.label || option.name || ''}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Allowed groups"
              placeholder="Select groups"
              sx={publicTextFieldSx}
            />
          )}
        />
      )}

      <Autocomplete
        multiple
        freeSolo
        options={cohort.allowedYearLevels || []}
        value={cohort.allowedYearLevels || []}
        onChange={(_event, nextValue) => {
          const normalizedLevels = Array.from(new Set(
            (nextValue || [])
              .map((level) => (level === null || level === undefined
                ? null
                : level.toString().trim().toLowerCase()))
              .filter(Boolean)
          ));
          handleCohortChange('allowedYearLevels', normalizedLevels);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Allowed year levels"
            placeholder="Add year level"
            sx={publicTextFieldSx}
          />
        )}
      />

      <TextField
        label="Access notes"
        multiline
        minRows={3}
        value={value?.accessNotes || ''}
        onChange={(event) => onChange({
          ...value,
          accessNotes: event.target.value,
        })}
        placeholder="Explain restrictions or scheduling context for other editors."
        sx={publicTextFieldSx}
      />

      <Divider textAlign="left">Provenance</Divider>

      <Box>
        <Typography variant="body2" color="text.secondary">
          Origin type: {provenance.originType || 'virtual-medic'}
        </Typography>
        {provenance.originTenantId && (
          <Typography variant="body2" color="text.secondary">
            Origin tenant ID: {provenance.originTenantId}
          </Typography>
        )}
        {provenance.lastSyncedFromOriginAt && (
          <Typography variant="body2" color="text.secondary">
            Last synced: {new Date(provenance.lastSyncedFromOriginAt).toLocaleString()}
          </Typography>
        )}
        {provenance.originNotes && (
          <Typography variant="body2" color="text.secondary">
            Notes: {provenance.originNotes}
          </Typography>
        )}
      </Box>
    </Paper>
  );
};

export default CaseVisibilityPanel;
